import type { StoreDetailsDto } from '@api/__generated__/accounts/accounts.schemas';
import { useGetStorefrontApiV1StoresStoreId } from '@api/__generated__/accounts/stores/stores';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useAuth } from '@hooks/useAuth';
import { delegationTokenConfig } from '@api/auth/config';
import { getAnonId } from '@utils/anonymousId';

export type MenuIcon = {
  order: number;
  key: string;
  element: ReactElement;
  showBadgeCount?: boolean;
};

interface AppContextValue {
  headerContent: ReactNode;
  setHeaderContent: (content: ReactNode) => void;
  menuIcons?: MenuIcon[];
  setMenuIcons: (icons: MenuIcon[]) => void;
  addMenuIcon: (icon: MenuIcon) => void;
  removeMenuIcon: (key: string) => void;
  selectedProductCount: number;
  setSelectedProductCount: (count: number) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  storeDetails: StoreDetailsDto | null;
  userData: { email: string; firstName: string; lastName: string } | null;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children, slug }: { children: ReactNode; slug?: string }) {
  const [headerContent, setHeaderContent] = useState<ReactNode>(null);
  const [menuIcons, setMenuIcons] = useState<MenuIcon[]>([]);
  const [selectedProductCount, setSelectedProductCount] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [userData, setUserData] = useState<{ email: string; firstName: string; lastName: string } | null>(null);

  const { isAuthenticated } = useAuth();
  const ldClient = useLDClient();

  const { data } = useGetStorefrontApiV1StoresStoreId({ storeId: Number(slug) }, { query: { enabled: !!slug, staleTime: Infinity } });
  const storeDetails: StoreDetailsDto | null = useMemo(() => data ?? null, [data]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${delegationTokenConfig.baseUrl}/me`, {
          credentials: 'include',
        });
        const data = await response.json();
        setUserData({ email: data.email, firstName: data.firstName, lastName: data.lastName });
      } catch {
        // ignore
      }
    };
    fetchUserInfo();
  }, []);

  useEffect(() => {
    if (!ldClient) return;
    if (isAuthenticated && userData) {
      ldClient.identify({
        kind: 'user',
        key: userData.email,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
      });
    } else if (!isAuthenticated) {
      ldClient.identify({
        kind: 'user',
        key: getAnonId(),
        anonymous: true,
      });
    }
  }, [isAuthenticated, userData, ldClient]);

  const set = useCallback((content: ReactNode) => {
    setHeaderContent(content);
  }, []);

  const addMenuIcon = useCallback((icon: MenuIcon) => {
    setMenuIcons((prev) => {
      const existingMenuIcon = prev.find((x) => x.key === icon.key);
      if (existingMenuIcon) {
        return prev;
      }
      return [...prev, icon];
    });
  }, []);

  const removeMenuIcon = useCallback((key: string) => {
    setMenuIcons((prev) => (prev ? prev.filter((x) => x.key !== key) : []));
  }, []);

  const value = useMemo(
    () => ({
      headerContent,
      setHeaderContent: set,
      menuIcons,
      setMenuIcons,
      addMenuIcon,
      removeMenuIcon,
      selectedProductCount,
      setSelectedProductCount,
      currentStep,
      setCurrentStep,
      storeDetails,
      userData,
    }),
    [headerContent, set, menuIcons, addMenuIcon, removeMenuIcon, selectedProductCount, currentStep, storeDetails, userData],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}
